<template>
  <recycle-scroller
    v-if="geofences.length > 0"
    id="geofence_list_recycle_scroller"
    ref="geofenceListRecycleScroller"
    class="geofence-list"
    :items="geofences"
    key-field="id"
    :item-size="46">
    <template #default="{ item }">
      <div
        class="geofence-list-item-container"
        @click="() => geofenceClicked(item)">
        <geofence-list-item
          :geofence="item"
          :is-editable="isEditable"
          :show-exclude-checkbox="showExcludeCheckbox"
          :is-selected="activeGeofence?.id === item?.id"
          :is-active="isActive(item)"
          @toggle-geofence="() => toggleGeofence(item)" />
      </div>
    </template>
  </recycle-scroller>
  <div
    v-else
    class="no-results">
    <div class="title">{{ $t('messages.noGeofences') }}</div>
    <div class="body">{{ $t('messages.noGeofencesDescription') }}</div>
    <div class="icon">
      <icon-colored-buildings />
    </div>
  </div>
</template>

<script setup lang="ts">
  import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

  import { computed, ComputedRef, ref } from 'vue';
  import { RecycleScroller } from 'vue-virtual-scroller';

  import { IconColoredBuildings } from '@/common/icons/colored';
  import Geofence from '@/common/models/geo/Geofence';

  import GeofenceListItem from './GeofenceListItem.vue';

  // region types
  interface IGeofenceListProps {
    activeGeofences: Geofence[];
    disabledGeofences: Geofence[];
    activeGeofence: Geofence;
    isEditable: boolean;
    showExcludeCheckbox: boolean;
  }

  interface IGeofenceListEmits {
    (e: 'selectGeofence', geofence: Geofence): void;
    (e: 'unselectGeofence', geofence: Geofence): void;
    (e: 'geofenceClicked', geofence: Geofence): void;
  }
  // endregion

  // region Props & Emits & Refs
  const props = defineProps<IGeofenceListProps>();

  const emit = defineEmits<IGeofenceListEmits>();

  const geofenceListRecycleScroller = ref(null);
  // endregion

  // region Computed
  const geofences: ComputedRef<Geofence[]> = computed(() =>
    [...props.activeGeofences, ...props.disabledGeofences].sort((a, b) =>
      a.id.toString().localeCompare(b.id.toString()),
    ),
  );
  // endregion

  // region Functions
  function isActive(geofence: Geofence): boolean {
    return props.activeGeofences.find((g) => geofence.id === g.id) != null;
  }

  function unselectGeofence(geofence: Geofence) {
    emit('unselectGeofence', geofence);
  }

  function selectGeofence(geofence: Geofence) {
    emit('selectGeofence', geofence);
  }

  function toggleGeofence(geofence: Geofence) {
    if (isActive(geofence)) {
      unselectGeofence(geofence);
    } else {
      selectGeofence(geofence);
    }
  }

  function geofenceClicked(geofence: Geofence) {
    emit('geofenceClicked', geofence);
  }

  function scrollToGeofenceListItem(id: number) {
    const index = geofences.value?.findIndex((x) => x.id === id);
    if (index !== -1 && index != null) {
      geofenceListRecycleScroller.value.scrollToItem(index);
    }
  }
  // endregion

  // region Exposed values
  defineExpose({
    scrollToGeofenceListItem,
  });
  // endregion
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;
  .geofence-list {
    font-size: 0.9em;
    padding-left: 0;

    .geofence-list-item-container {
      border-bottom: solid 0.1rem $borders-light;
      cursor: pointer;
    }
  }

  .no-results {
    color: var(--textMutedColor);

    padding: 3.2rem 3.2rem;

    .title {
      font-weight: 700;
      font-size: 1.5rem;
    }

    .body {
      font-size: 1.3rem;
      margin-top: 0.6rem;
    }

    .icon {
      margin-top: 2.4rem;
      opacity: 0.75;
    }
  }
</style>
